import { 
  GA_MEASUREMENT_ID, 
  ANALYTICS_CONFIG, 
  ANALYTICS_EVENTS, 
  USER_PROPERTIES 
} from '../config/analytics';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Initialize Google Analytics
export const initializeAnalytics = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('Google Analytics Measurement ID not found');
    return false;
  }

  try {
    // Create script tags
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(gtagScript);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    (window as any).gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configure gtag
    (window as any).gtag('js', new Date());
    (window as any).gtag('config', GA_MEASUREMENT_ID, ANALYTICS_CONFIG);
    
    console.log('✅ Google Analytics initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Google Analytics:', error);
    return false;
  }
};

// Track page views
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (!GA_MEASUREMENT_ID || !(window as any).gtag) return;
  
  try {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
    
    console.log('📊 Page view tracked:', pagePath);
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track custom events
export const trackEvent = (
  eventName: string, 
  parameters: Record<string, any> = {}
) => {
  if (!GA_MEASUREMENT_ID || !(window as any).gtag) return;
  
  try {
    (window as any).gtag('event', eventName, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label,
      value: parameters.value,
      ...parameters
    });
    
    console.log('📊 Event tracked:', eventName, parameters);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track user login
export const trackUserLogin = (method: string, userId?: string) => {
    trackEvent(ANALYTICS_EVENTS.USER_LOGIN, {
        method,
        user_id: userId,
        category: 'authentication'
    });

    if (userId) {
        setUserId(userId);
    }
};

// Track user signup
export const trackUserSignup = (method: string, userId?: string) => {
    trackEvent(ANALYTICS_EVENTS.USER_SIGNUP, {
        method,
        user_id: userId,
        category: 'authentication'
    });

    if (userId) {
        setUserId(userId);
    }
};

// Set user ID
export const setUserId = (userId: string) => {
  if (!GA_MEASUREMENT_ID || !(window as any).gtag) return;
  
  try {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId
    });
    console.log('👤 User ID set:', userId);
  } catch (error) {
    console.error('Failed to set user ID:', error);
  }
};

// Set user properties
export const setUserProperty = (propertyName: string, value: string) => {
  if (!GA_MEASUREMENT_ID || !(window as any).gtag) return;
  
  try {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      custom_map: {
        [propertyName]: value
      }
    });
    console.log('👤 User property set:', propertyName, value);
  } catch (error) {
    console.error('Failed to set user property:', error);
  }
};

// Enhanced Learning Analytics
export const LearningAnalytics = {
    // Grammar lesson tracking
    startGrammarLesson: (topic: string, difficulty: string) => {
        trackEvent(ANALYTICS_EVENTS.GRAMMAR_LESSON_START, {
            lesson_type: 'grammar',
            topic,
            difficulty,
            category: 'learning'
        });
    },

    completeGrammarLesson: (topic: string, difficulty: string, duration: number, score?: number) => {
        trackEvent(ANALYTICS_EVENTS.GRAMMAR_LESSON_COMPLETE, {
            lesson_type: 'grammar',
            topic,
            difficulty,
            duration_seconds: duration,
            score,
            category: 'learning'
        });
    },

    // Sentence practice tracking
    startSentencePractice: (wordCount: number) => {
        trackEvent(ANALYTICS_EVENTS.SENTENCE_PRACTICE_START, {
            lesson_type: 'sentence',
            word_count: wordCount,
            category: 'learning'
        });
    },

    completeSentencePractice: (wordCount: number, duration: number, correctAnswers: number, totalQuestions: number) => {
        const accuracy = (correctAnswers / totalQuestions) * 100;

        trackEvent(ANALYTICS_EVENTS.SENTENCE_PRACTICE_COMPLETE, {
            lesson_type: 'sentence',
            word_count: wordCount,
            duration_seconds: duration,
            accuracy_percentage: accuracy,
            correct_answers: correctAnswers,
            total_questions: totalQuestions,
            category: 'learning'
        });
    },

    // Word memorization tracking
    memorizeWord: (word: string, difficulty: string, attempts: number) => {
        trackEvent(ANALYTICS_EVENTS.WORD_MEMORIZED, {
            word,
            difficulty,
            attempts,
            category: 'learning'
        });
    },

    // Language selection
    changeLanguage: (fromLanguage: string, toLanguage: string) => {
        trackEvent(ANALYTICS_EVENTS.LANGUAGE_CHANGED, {
            from_language: fromLanguage,
            to_language: toLanguage,
            category: 'user_preference'
        });

        setUserProperty(USER_PROPERTIES.SELECTED_LANGUAGE, toLanguage);
    },

    // AI features usage
    useGrammarCheck: (originalText: string, hasErrors: boolean) => {
        trackEvent(ANALYTICS_EVENTS.GRAMMAR_CHECK_USED, {
            text_length: originalText.length,
            has_errors: hasErrors,
            category: 'ai_features'
        });
    },

    useTranslation: (fromLanguage: string, toLanguage: string, textLength: number) => {
        trackEvent(ANALYTICS_EVENTS.TRANSLATION_USED, {
            from_language: fromLanguage,
            to_language: toLanguage,
            text_length: textLength,
            category: 'ai_features'
        });
    },

    // Engagement tracking
    addFavorite: (itemType: string, itemId: string) => {
        trackEvent(ANALYTICS_EVENTS.FAVORITE_ADDED, {
            item_type: itemType,
            item_id: itemId,
            category: 'engagement'
        });
    },

    performSearch: (query: string, resultCount: number) => {
        trackEvent(ANALYTICS_EVENTS.SEARCH_PERFORMED, {
            search_term: query,
            result_count: resultCount,
            category: 'engagement'
        });
    },

    // Progress milestones
    reachMilestone: (milestoneType: string, value: number) => {
        trackEvent(ANALYTICS_EVENTS.PROGRESS_MILESTONE, {
            milestone_type: milestoneType,
            milestone_value: value,
            category: 'achievement'
        });
    }
};

// Error tracking
export const trackError = (errorType: string, errorMessage: string, errorLocation?: string) => {
    trackEvent(ANALYTICS_EVENTS.API_ERROR, {
        error_type: errorType,
        error_message: errorMessage,
        error_location: errorLocation,
        category: 'error'
    });
};

// Session tracking
export const trackSessionStart = () => {
    trackEvent('session_start', {
        category: 'engagement'
    });
};

export const trackSessionEnd = (duration: number) => {
    trackEvent('session_end', {
        session_duration: duration,
        category: 'engagement'
    });
};

// Enhanced ecommerce for learning progress
export const trackLearningProgress = (
    itemId: string,
    itemName: string,
    itemCategory: string,
    progress: number
) => {
    trackEvent('progress_update', {
        item_id: itemId,
        item_name: itemName,
        item_category: itemCategory,
        progress_percentage: progress,
        category: 'learning_progress'
    });
}; 